import { useMemoizedFn, useMount } from 'ahooks';
import { Checkbox, Col, Form, Input, Radio, Row } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useAppDispatch } from 'app/hooks';
import { JobconfigVersionDeleteIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { SectionHeading, UnderlineText } from 'components/RevxTypography';
import {
  FinishesItem,
  InkFinishes,
  StepActionTypeEnum,
} from 'domains/skuDomain';
import { cloneDeep } from 'lodash-es';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { StepComponentProps } from '.';
import './Step6InkFinishes.less';

export const Step6InkFinishes = ({
  sku,
  topSku,
  skuPart,
}: StepComponentProps) => {
  const { t } = useTranslation();
  const [form] = useForm();

  const dispatch = useAppDispatch();
  const [inkFinishes, setInkFinishes] = useState<InkFinishes>();
  const [hasPmsFront, setHasPmsFront] = useState<Boolean>();
  const [hasPmsBack, setHasPmsBack] = useState<Boolean>();
  const [pmsFront, setPmsFront] = useState<string[]>();
  const [pmsBack, setPmsBack] = useState<string[]>();
  const [disabledPmsFront, setDisabledPmsFront] = useState<Boolean>();
  const [disabledPmsBack, setDisabledPmsBack] = useState<Boolean>();

  const formItemLayout = {
    labelCol: { span: 4 },
    wrapperCol: { span: 20 },
  };

  useMount(async () => {
    dispatch.sku.setRevxSpinloading(true);
    try {
      dispatch.sku.registerStepAction({
        [StepActionTypeEnum.STEP6_INKFINISHES]: submit,
      });
      setHasPmsFront(Boolean(sku.hasPmsFront));
      setHasPmsBack(Boolean(sku.hasPmsBack));
      setPmsFront(sku.pmsFront);
      setPmsBack(sku.pmsBack);
      setDisabledPmsFront(Boolean((sku.pmsFront || []).length >= 8));
      setDisabledPmsBack(Boolean((sku.pmsBack || []).length >= 8));
      const defaultValue = {
        coatingBack: sku.coatingBack,
        coatingFront: sku.coatingFront,
        hasPmsBack: [sku.hasPmsBack + ''],
        hasPmsFront: [sku.hasPmsFront + ''],
        inkBack: sku.inkBack,
        inkFront: sku.inkFront,
        skipInkAndFinish: Boolean(sku.skipInkAndFinish),
        pmsFrontEditable: Boolean(sku.pmsFrontEditable),
        pmsBackEditable: Boolean(sku.pmsBackEditable),
        pmsFront: sku.pmsFront || [],
        pmsBack: sku.pmsBack || [],
      };
      form.setFieldsValue(defaultValue);
      const res = await dispatch.sku.getInk();
      setInkFinishes(res);

      dispatch.sku.setRevxSpinloading(false);
    } catch {
      dispatch.sku.setRevxSpinloading(false);
    }
  });

  const submit = useMemoizedFn(async () => {
    if (sku.readonly) {
      return true;
    }
    try {
      const formValue = cloneDeep(await form.getFieldsValue());
      if (!formValue.coatingBack) {
        formValue.coatingBack = [];
      }
      if (!formValue.coatingFront) {
        formValue.coatingFront = [];
      }
      if (!formValue.coatingBack.includes('none')) {
        formValue.coatingBack.push('none');
      }
      if (!formValue.coatingFront.includes('none')) {
        formValue.coatingFront.push('none');
      }
      if (!formValue.inkBack.includes('none')) {
        formValue.inkBack.push('none');
      }
      if (!formValue.inkFront.includes('none')) {
        formValue.inkFront.push('none');
      }
      return dispatch.sku.updateInk({
        id: sku.id,
        version: sku.version,
        ...formValue,
        hasPmsBack: Boolean((formValue.hasPmsBack || [])[0] === 'true'),
        hasPmsFront: Boolean((formValue.hasPmsFront || [])[0] === 'true'),
        pmsFrontEditable: Boolean(formValue.pmsFrontEditable),
        pmsBackEditable: Boolean(formValue.pmsBackEditable),
        pmsFront: Boolean((formValue.hasPmsFront || [])[0] === 'true')
          ? formValue.pmsFront || []
          : [],
        pmsBack: Boolean((formValue.hasPmsBack || [])[0] === 'true')
          ? formValue.pmsBack || []
          : [],
      });
    } catch {
      // do nothing when form invalid
      return false;
    }
  });

  const organizeStructure = (arr: FinishesItem[] | undefined) => {
    return (
      arr?.filter((item) => item.value !== 'none' && item.value !== 'false') ||
      []
    );
  };

  const formValuesChange = (changedValues: any, allValue: any) => {
    setHasPmsFront(Boolean(allValue.hasPmsFront[0] === 'true'));
    setHasPmsBack(Boolean(allValue.hasPmsBack[0] === 'true'));
    setPmsFront(allValue.pmsFront);
    setPmsBack(allValue.pmsBack);
    setDisabledPmsFront(Boolean((allValue.pmsFront || []).length >= 8));
    setDisabledPmsBack(Boolean((allValue.pmsBack || []).length >= 8));
  };

  return (
    <>
      <div className="single-sku-config-wizard-step-title">
        <span className="brand-green">
          {t('platform.sku.wizard.whichColorsAndFinishes')}
        </span>{' '}
        {t('platform.sku.wizard.areSupported')}{' '}
      </div>
      <div className="single-sku-config-wizard-step-content">
        <SectionHeading>{t('platform.sku.wizard.ink.title')}.</SectionHeading>
        <p>{t('platform.sku.wizard.ink.subTitle')}.</p>
        <Form form={form} {...formItemLayout} onValuesChange={formValuesChange}>
          <Form.Item
            labelCol={{
              flex: 'none',
            }}
            wrapperCol={{
              flex: 'none',
            }}
            label={t('platform.sku.wizard.quantity.form.label.inkFinishesSkip')}
            name="skipInkAndFinish"
          >
            <Radio.Group disabled={sku.readonly || Boolean(topSku)}>
              <Radio value={true}>
                <span>Yes</span>
              </Radio>
              <Radio value={false}>
                <span>No</span>
              </Radio>
            </Radio.Group>
          </Form.Item>
          <div>
            <RevxFlexLayout style={{ marginBottom: '10px' }}>
              <SectionHeading style={{ marginBottom: 0 }}>
                {t('platform.sku.wizard.ink.inkFront')}:
              </SectionHeading>

              <RevxButton
                size="small"
                type="link"
                brandGreen
                style={{
                  marginLeft: '10px',
                }}
                onClick={() => {
                  const formData = form.getFieldsValue();
                  form.setFieldsValue({
                    ...formData,
                    coatingFront: organizeStructure(inkFinishes?.finishes).map(
                      (item) => item.value
                    ),
                    hasPmsFront: organizeStructure(inkFinishes?.pms).map(
                      (item) => item.value
                    ),
                    inkFront: organizeStructure(inkFinishes?.inks).map(
                      (item) => item.value
                    ),
                  });
                }}
              >
                {t('common.selectAll')}
              </RevxButton>

              <RevxButton
                size="small"
                type="link"
                brandGreen
                style={{
                  marginLeft: '10px',
                }}
                onClick={() => {
                  const formData = form.getFieldsValue();
                  form.setFieldsValue({
                    ...formData,
                    coatingFront: [],
                    hasPmsFront: [],
                    inkFront: [],
                  });
                }}
              >
                {t('common.deselectAll')}
              </RevxButton>
            </RevxFlexLayout>

            <Form.Item label={'Finishes'} name="coatingFront">
              <Checkbox.Group style={{ width: '100%' }} disabled={sku.readonly}>
                <Row>
                  {organizeStructure(inkFinishes?.finishes).map((item) => (
                    <Col span={8} key={item.value + item.label}>
                      <Checkbox value={item.value}>{item.label}</Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            </Form.Item>

            <Form.Item label={'PMS'} name="hasPmsFront">
              <Checkbox.Group style={{ width: '100%' }} disabled={sku.readonly}>
                <Row>
                  {organizeStructure(inkFinishes?.pms).map((item) => (
                    <Col span={8} key={item.value + item.label}>
                      <Checkbox value={item.value}>{item.label}</Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            </Form.Item>
            <div hidden={!hasPmsFront}>
              <Row>
                <Col offset={4} className="ant-form-item-label">
                  <label>Pantone colors</label>
                </Col>
              </Row>
              <Form.List name="pmsFront">
                {(fields, { add, remove }) => (
                  <Row className="ant-form-item">
                    <Col span={20} offset={4}>
                      {fields.map((field, index) => (
                        <RevxFlexLayout className="ink-finish-item">
                          <Form.Item {...field} noStyle>
                            <Input
                              maxLength={50}
                              style={{ width: '200px', marginRight: '10px' }}
                            />
                          </Form.Item>
                          <div
                            className="ink-finish-delete"
                            onClick={() => {
                              remove(field.name);
                            }}
                          >
                            <JobconfigVersionDeleteIcon />
                          </div>
                        </RevxFlexLayout>
                      ))}
                    </Col>
                    {!disabledPmsFront && (
                      <Col span={20} offset={4}>
                        <RevxFlexLayout
                          align="center"
                          className="ink-finish-add"
                        >
                          <UnderlineText
                            color={'green'}
                            onClick={() => {
                              add();
                            }}
                          >
                            +
                            <span>
                              <Trans i18nKey="jobConfig.additionalOption.addColor"></Trans>
                            </span>
                          </UnderlineText>
                        </RevxFlexLayout>
                      </Col>
                    )}
                  </Row>
                )}
              </Form.List>
            </div>
            {Boolean(pmsFront?.length && hasPmsFront) && (
              <Row align="middle" style={{ marginBottom: '24px' }}>
                <Col offset={4} className="ant-form-item-label">
                  <label>Are these values editable?</label>
                </Col>
                <Col span={14} style={{ height: '32px' }}>
                  <Form.Item
                    label={' '}
                    className="ink-finish-item"
                    name="pmsFrontEditable"
                    labelCol={{ span: 0 }}
                    wrapperCol={{ span: 19 }}
                  >
                    <Radio.Group
                      style={{ width: '100%' }}
                      disabled={sku.readonly}
                    >
                      <Row>
                        <Col span={8}>
                          <Radio value={true}>Yes</Radio>
                          <Radio value={false}>No</Radio>
                        </Col>
                      </Row>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Form.Item label={'Inks'} name="inkFront">
              <Checkbox.Group style={{ width: '100%' }} disabled={sku.readonly}>
                <Row>
                  {organizeStructure(inkFinishes?.inks).map((item) => (
                    <Col span={8} key={item.value + item.label}>
                      <Checkbox value={item.value}>{item.label}</Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            </Form.Item>
          </div>
          <div>
            <RevxFlexLayout style={{ marginBottom: '10px' }}>
              <SectionHeading style={{ marginBottom: 0 }}>
                {t('platform.sku.wizard.ink.inkBack')}:
              </SectionHeading>

              <RevxButton
                size="small"
                type="link"
                brandGreen
                style={{
                  marginLeft: '10px',
                }}
                onClick={() => {
                  const formData = form.getFieldsValue();
                  form.setFieldsValue({
                    ...formData,
                    coatingBack: organizeStructure(inkFinishes?.finishes).map(
                      (item) => item.value
                    ),
                    hasPmsBack: organizeStructure(inkFinishes?.pms).map(
                      (item) => item.value
                    ),
                    inkBack: organizeStructure(inkFinishes?.inks).map(
                      (item) => item.value
                    ),
                  });
                }}
              >
                {t('common.selectAll')}
              </RevxButton>

              <RevxButton
                size="small"
                type="link"
                brandGreen
                style={{
                  marginLeft: '10px',
                }}
                onClick={() => {
                  const formData = form.getFieldsValue();
                  form.setFieldsValue({
                    ...formData,
                    coatingBack: [],
                    hasPmsBack: [],
                    inkBack: [],
                  });
                }}
              >
                {t('common.deselectAll')}
              </RevxButton>
            </RevxFlexLayout>

            <Form.Item label={'Finishes'} name="coatingBack">
              <Checkbox.Group style={{ width: '100%' }} disabled={sku.readonly}>
                <Row>
                  {organizeStructure(inkFinishes?.finishes).map((item) => (
                    <Col span={8} key={item.value + item.label}>
                      <Checkbox value={item.value}>{item.label}</Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            </Form.Item>

            <Form.Item label={'PMS'} name="hasPmsBack">
              <Checkbox.Group style={{ width: '100%' }} disabled={sku.readonly}>
                <Row>
                  {organizeStructure(inkFinishes?.pms).map((item) => (
                    <Col span={8} key={item.value + item.label}>
                      <Checkbox value={item.value}>{item.label}</Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            </Form.Item>

            <div hidden={!hasPmsBack}>
              <Row>
                <Col offset={4} className="ant-form-item-label">
                  <label>Pantone colors</label>
                </Col>
              </Row>
              <Form.List name="pmsBack">
                {(fields, { add, remove }) => (
                  <Row className="ant-form-item">
                    <Col span={20} offset={4}>
                      {fields.map((field, index) => (
                        <RevxFlexLayout className="ink-finish-item">
                          <Form.Item {...field} noStyle>
                            <Input
                              maxLength={50}
                              style={{ width: '200px', marginRight: '10px' }}
                            />
                          </Form.Item>
                          <div
                            className="ink-finish-delete"
                            onClick={() => {
                              remove(field.name);
                            }}
                          >
                            <JobconfigVersionDeleteIcon />
                          </div>
                        </RevxFlexLayout>
                      ))}
                    </Col>
                    {!disabledPmsBack && (
                      <Col span={20} offset={4}>
                        <RevxFlexLayout
                          align="center"
                          className="ink-finish-add"
                        >
                          <UnderlineText
                            color={'green'}
                            onClick={() => {
                              add();
                            }}
                          >
                            +
                            <span>
                              <Trans i18nKey="jobConfig.additionalOption.addColor"></Trans>
                            </span>
                          </UnderlineText>
                        </RevxFlexLayout>
                      </Col>
                    )}
                  </Row>
                )}
              </Form.List>
            </div>

            {Boolean(pmsBack?.length && hasPmsBack) && (
              <Row align="middle" style={{ marginBottom: '24px' }}>
                <Col offset={4} className="ant-form-item-label">
                  <label>Are these values editable?</label>
                </Col>
                <Col span={14} style={{ height: '32px' }}>
                  <Form.Item
                    label={' '}
                    className="ink-finish-item"
                    name="pmsBackEditable"
                    labelCol={{ span: 0 }}
                    wrapperCol={{ span: 19 }}
                  >
                    <Radio.Group
                      style={{ width: '100%' }}
                      disabled={sku.readonly}
                    >
                      <Row>
                        <Col span={8}>
                          <Radio value={true}>Yes</Radio>
                          <Radio value={false}>No</Radio>
                        </Col>
                      </Row>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Form.Item label={'Inks'} name="inkBack">
              <Checkbox.Group style={{ width: '100%' }} disabled={sku.readonly}>
                <Row>
                  {organizeStructure(inkFinishes?.inks).map((item) => (
                    <Col span={8} key={item.value + item.label}>
                      <Checkbox value={item.value}>{item.label}</Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            </Form.Item>
          </div>
        </Form>
      </div>
    </>
  );
};
